import React, { useState, useEffect } from 'react';
import subscriptionService from '../../utils/services/subscriptionService';
import Toast from '../Toast/Toast';
import PreviewSubscriptionChangeModal from './PreviewSubscriptionChangeModal';
import aflLogo from '../../assets/afl_logo.png';
import nbaLogo from '../../assets/nba_logo.png';

function ManageExistingSportSubscription({ isOpen, onClose, subscription, onSubscriptionChanged }) {
    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const [isCancellingDowngrade, setIsCancellingDowngrade] = useState(false);
    const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);

    useEffect(() => {
        if (isOpen && subscription) {
            fetchPlans();
        }
    }, [isOpen, subscription]);

    const fetchPlans = async () => {
        setIsLoading(true);
        setError(null);
        const result = await subscriptionService.getAllPlans();
        
        if (result.success) {
            setPlans(result.data);
        } else {
            setError(result.message);
        }
        
        setIsLoading(false);
    };

    const getSportLogo = (sport) => {
        const sportLogos = {
            'NBA': nbaLogo,
            'AFL': aflLogo,
            'ALL': 'icon', // Use lightning bolt icon instead of logo
        };
        return sportLogos[sport] || null;
    };

    // Define tier hierarchy (higher number = higher tier)
    const getTierLevel = (tier) => {
        const tierLevels = {
            'BASIC': 1,
            'PRO': 2,
            'ELITE': 3,
            'EV_FOUNDERS': 4,
            'LIFETIME': 5,
        };
        return tierLevels[tier] || 0;
    };

    // Determine if plan is upgrade or downgrade
    const getPlanAction = (planTier) => {
        if (!subscription) return 'current';
        
        const currentLevel = getTierLevel(subscription.tier);
        const planLevel = getTierLevel(planTier);
        
        if (planLevel > currentLevel) return 'upgrade';
        if (planLevel < currentLevel) return 'downgrade';
        return 'current';
    };

    const handleUpgrade = async (plan) => {
        setSelectedPlan(plan);
        setIsPreviewModalOpen(true);
        setIsPreviewLoading(true);
        setPreviewError(null);
        setPreviewData(null);

        const result = await subscriptionService.previewSubscriptionChange(plan.key);
        
        setIsPreviewLoading(false);

        if (result.success) {
            setPreviewData(result.data);
        } else {
            setPreviewError(result.message);
        }
    };

    const handleDowngrade = async (plan) => {
        setSelectedPlan(plan);
        setIsPreviewModalOpen(true);
        setIsPreviewLoading(true);
        setPreviewError(null);
        setPreviewData(null);

        const result = await subscriptionService.previewSubscriptionChange(plan.key);
        
        setIsPreviewLoading(false);

        if (result.success) {
            setPreviewData(result.data);
        } else {
            setPreviewError(result.message);
        }
    };

    const handleConfirmChange = async () => {
        if (!selectedPlan) return;

        setIsConfirming(true);

        const result = await subscriptionService.changeSubscription(selectedPlan.key);

        if (result.success) {
            const { data, message } = result;
            
            // Determine success message based on change type
            let successMessage = message;
            let changeType = 'success';
            
            if (data?.change_type === 'upgrade') {
                successMessage = `Successfully upgraded to ${data.new_plan.display_name}! You now have immediate access to all features.`;
                changeType = 'upgrade';
            } else if (data?.change_type === 'downgrade') {
                successMessage = `Downgrade to ${data.new_plan.display_name} scheduled for ${new Date(data.billing.downgrade_effective_date).toLocaleDateString()}. You'll keep your current plan until then.`;
                changeType = 'downgrade';
            } else if (data?.action === 'cancel_scheduled_change') {
                successMessage = `Scheduled downgrade cancelled. You'll continue with ${data.current_plan.display_name} indefinitely.`;
                changeType = 'cancel';
            }

            // Wait 3 seconds for webhook to process and update database
            await new Promise(resolve => setTimeout(resolve, 3000));

            setIsConfirming(false);

            // Close both modals
            setIsPreviewModalOpen(false);
            onClose();

            // Notify parent with success message
            if (onSubscriptionChanged) {
                onSubscriptionChanged(successMessage, changeType);
            }
        } else {
            setIsConfirming(false);
            // Show error toast in modal
            setToast({ message: result.message || 'Failed to change subscription', type: 'error' });
        }
    };

    const handleCancelScheduledDowngrade = async () => {
        if (!subscription) return;

        setIsCancellingDowngrade(true);

        // Call API with current subscription key to cancel the scheduled downgrade
        const result = await subscriptionService.changeSubscription(subscription.key);

        if (result.success) {
            const { data, message } = result;
            
            let successMessage = message;
            if (data?.action === 'cancel_scheduled_change') {
                successMessage = `Scheduled downgrade cancelled. You'll continue with ${data.current_plan.display_name} indefinitely.`;
            }

            // Wait 3 seconds for webhook to process and update database
            await new Promise(resolve => setTimeout(resolve, 3000));

            setIsCancellingDowngrade(false);

            // Close modal
            onClose();

            // Notify parent with success message
            if (onSubscriptionChanged) {
                onSubscriptionChanged(successMessage, 'cancel');
            }
        } else {
            setIsCancellingDowngrade(false);
            // Show error toast
            setToast({ message: result.message || 'Failed to cancel scheduled downgrade', type: 'error' });
        }
    };

    const handleCancelSubscription = async () => {
        if (!subscription) return;

        setIsCancellingSubscription(true);

        // Call API to create Stripe Customer Portal session with cancellation flow
        const result = await subscriptionService.createPortalSession(subscription.key, 'subscription_cancel');

        if (result.success) {
            // Redirect to Stripe Customer Portal cancellation page
            window.location.href = result.data.url;
        } else {
            setIsCancellingSubscription(false);
            // Show error toast
            setToast({ 
                message: result.message || 'Failed to open customer portal. Please try again.', 
                type: 'error' 
            });
        }
    };

    const closePreviewModal = () => {
        setIsPreviewModalOpen(false);
        setPreviewData(null);
        setPreviewError(null);
        setSelectedPlan(null);
    };

    const closeToast = () => {
        setToast(null);
    };

    if (!isOpen || !subscription) return null;

    // Filter plans for the current sport only
    const sportPlans = plans.find(p => p.sport === subscription.sport);

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div 
                className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
                onClick={(isCancellingDowngrade || isCancellingSubscription) ? undefined : onClose}
            />

            {/* Modal Container */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl shadow-2xl max-w-5xl w-full border border-slate-800/50 max-h-[90vh] overflow-hidden">
                    {/* Header */}
                    <div className="sticky top-0 z-10 bg-gradient-to-br from-[#0f172a] to-[#020617] border-b border-slate-800/50 px-6 py-4">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex-1 flex flex-col items-center gap-2">
                                <h2 className="text-2xl font-bold text-white">Manage {subscription.sport} Subscription</h2>
                                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 px-4 py-2 rounded-lg">
                                    <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-sm font-semibold text-emerald-400">
                                        {subscription.display_name || `${subscription.sport} ${subscription.tier}`}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                disabled={isCancellingDowngrade || isCancellingSubscription}
                                className="text-slate-400 hover:text-white transition-colors duration-200 absolute right-6 top-4 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
                                    <p className="mt-4 text-slate-400">Loading plans...</p>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="text-center py-12">
                                <div className="text-red-400 mb-4">
                                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <p className="text-lg font-semibold text-white mb-2">Error loading plans</p>
                                <p className="text-slate-400 mb-4">{error}</p>
                                <button
                                    onClick={fetchPlans}
                                    className="bg-emerald-600 hover:bg-emerald-700 px-6 py-2 rounded-lg font-semibold transition duration-200"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : !sportPlans ? (
                            <div className="text-center py-12">
                                <p className="text-slate-400 text-lg">No plans available for {subscription.sport}</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Scheduled Change Info Banner */}
                                {subscription.scheduled_change && (
                                    <div className="bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 mt-0.5">
                                                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-semibold text-blue-300 mb-1">Scheduled Plan Change</p>
                                                <p className="text-sm text-slate-300 mb-3">
                                                    You have a scheduled downgrade to <span className="font-semibold text-white">{subscription.scheduled_change.to_display_name}</span> on {subscriptionService.formatDate(subscription.scheduled_change.effective_date)}. You can change or cancel this downgrade below.
                                                </p>
                                                <button
                                                    onClick={handleCancelScheduledDowngrade}
                                                    disabled={isCancellingDowngrade}
                                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-800/50 hover:bg-slate-700/70 border border-blue-500/50 hover:border-blue-500/70 text-slate-300 hover:text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isCancellingDowngrade ? (
                                                        <>
                                                            <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-transparent"></div>
                                                            <span>Cancelling...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                            <span>Cancel Scheduled Downgrade</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Plans Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {sportPlans.plans
                                        .filter(plan => plan.show !== false)
                                        .map((plan) => {
                                            const isCurrent = plan.tier === subscription.tier;
                                            const isScheduled = subscription.scheduled_change?.to_plan_key === plan.key;
                                            const isSoldOut = !plan.is_available;
                                            const hasTrial = plan.trial_days > 0;
                                            const hasLimitedSpots = !plan.unlimited && plan.max_spots && plan.spots_remaining > 0;
                                            const planAction = getPlanAction(plan.tier);
                                            const canPurchase = plan.can_purchase !== false;

                                            return (
                                                <div
                                                    key={plan.key}
                                                    className={`relative bg-gradient-to-br from-[#0f172a]/50 to-[#020617]/50 rounded-xl p-5 border transition-all duration-300 group flex flex-col ${
                                                        isCurrent 
                                                            ? 'border-emerald-500/50 ring-2 ring-emerald-500/30'
                                                            : isScheduled
                                                            ? 'border-blue-500/50 ring-2 ring-blue-500/30'
                                                            : 'border-slate-800/50 hover:border-emerald-500/30'
                                                    }`}
                                                >
                                                    {/* Top Badges */}
                                                    <div className="flex items-center gap-2 mb-3 flex-wrap min-h-[28px]">
                                                        {isCurrent && (
                                                            <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                ✓ Current Plan
                                                            </span>
                                                        )}
                                                        {isScheduled && (
                                                            <span className="bg-blue-500/20 text-blue-400 border border-blue-500/30 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1">
                                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                </svg>
                                                                Scheduled Plan
                                                            </span>
                                                        )}
                                                        {isSoldOut && (
                                                            <span className="bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                Sold Out
                                                            </span>
                                                        )}
                                                        {!isSoldOut && !isCurrent && !isScheduled && hasTrial && (
                                                            <span className="bg-blue-500/20 text-blue-400 border border-blue-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                {plan.trial_days}-Day Trial
                                                            </span>
                                                        )}
                                                        {!isSoldOut && hasLimitedSpots && (
                                                            <span className="bg-purple-500/20 text-purple-400 border border-purple-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                {plan.spots_remaining} spots left
                                                            </span>
                                                        )}
                                                        {!isSoldOut && plan.unlimited && (
                                                            <span className="bg-purple-500/20 text-purple-400 border border-purple-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                Unlimited spots
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Sport Logo and Plan Title */}
                                                    <div className="mb-4 text-center">
                                                        {getSportLogo(subscription.sport) && (
                                                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 mx-auto ${
                                                                subscription.sport === 'ALL' 
                                                                    ? 'bg-purple-500/20 border border-purple-500/30' 
                                                                    : 'bg-white'
                                                            }`}>
                                                                {getSportLogo(subscription.sport) === 'icon' ? (
                                                                    <svg className="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 24 24">
                                                                        <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
                                                                    </svg>
                                                                ) : (
                                                                    <img 
                                                                        src={getSportLogo(subscription.sport)} 
                                                                        alt={`${subscription.sport} Logo`}
                                                                        className="w-full h-full object-contain p-1"
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                        <h4 className="text-lg font-bold text-white">
                                                            {plan.display_name || `${subscription.sport} ${plan.tier}`}
                                                        </h4>
                                                    </div>

                                                    {/* Price */}
                                                    <div className="mb-4">
                                                        {plan.monthly_price ? (
                                                            <div className="flex items-baseline justify-center">
                                                                <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                                                                    ${parseFloat(plan.monthly_price).toFixed(2)}
                                                                </span>
                                                                <span className="text-slate-400 text-sm ml-2">/month</span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                                                                Lifetime Access
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Features */}
                                                    <div className="space-y-2 mb-4 flex-grow">
                                                        {plan.unlimited === true && (
                                                            <div className="flex items-center text-sm text-slate-400">
                                                                <svg className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                </svg>
                                                                Unlimited capacity
                                                            </div>
                                                        )}
                                                        {!plan.unlimited && plan.max_spots !== null && plan.max_spots !== undefined && (
                                                            <div className="flex items-center text-sm text-slate-400">
                                                                <svg className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                </svg>
                                                                Limited to {plan.max_spots} members
                                                            </div>
                                                        )}
                                                        {!isSoldOut && hasTrial && (
                                                            <div className="flex items-center text-sm text-slate-400">
                                                                <svg className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                </svg>
                                                                {plan.trial_days} days free trial
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Action Button */}
                                                    {isCurrent ? (
                                                        <div className="w-full py-2.5 rounded-lg font-semibold bg-slate-700/50 text-slate-400 flex items-center justify-center gap-2 cursor-not-allowed">
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                            </svg>
                                                            Current Plan
                                                        </div>
                                                    ) : isScheduled ? (
                                                        <div className="w-full py-2.5 rounded-lg font-semibold bg-blue-500/20 border border-blue-500/30 text-blue-300 flex items-center justify-center gap-2 cursor-not-allowed">
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            Already Scheduled
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => planAction === 'upgrade' ? handleUpgrade(plan) : handleDowngrade(plan)}
                                                            disabled={isSoldOut || !canPurchase}
                                                            className={`w-full py-2.5 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                                                                isSoldOut || !canPurchase
                                                                    ? 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
                                                                    : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/20'
                                                            }`}
                                                        >
                                                            {isSoldOut ? (
                                                                <>
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                                    </svg>
                                                                    Sold Out
                                                                </>
                                                            ) : !canPurchase ? (
                                                                <>
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                    </svg>
                                                                    Coming Soon
                                                                </>
                                                            ) : planAction === 'upgrade' ? (
                                                                <>
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                                    </svg>
                                                                    Upgrade Plan
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                                                                    </svg>
                                                                    Downgrade Plan
                                                                </>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>

                                {/* Cancel Subscription Button */}
                                <div className="mt-8 pt-6 border-t border-slate-800/50">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-slate-400">
                                                Need to cancel? You will be redirected to Stripe for secure processing. Canceling your subscription will end your access at the end of the current billing period. You can resubscribe at any time.
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleCancelSubscription}
                                            disabled={isCancellingSubscription}
                                            className="bg-red-900/70 hover:bg-red-800/80 border border-red-800/50 text-red-200 hover:text-white text-sm font-medium transition-all duration-200 flex items-center gap-1.5 flex-shrink-0 ml-4 px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isCancellingSubscription ? (
                                                <>
                                                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-red-200 border-t-transparent"></div>
                                                    <span>Loading...</span>
                                                </>
                                            ) : (
                                                <>
                                                    Cancel Subscription
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Toast Notification */}
            {toast && (
                <Toast 
                    message={toast.message}
                    type={toast.type}
                    onClose={closeToast}
                />
            )}

            {/* Preview Subscription Change Modal */}
            <PreviewSubscriptionChangeModal 
                isOpen={isPreviewModalOpen}
                onClose={closePreviewModal}
                onConfirm={handleConfirmChange}
                previewData={previewData}
                isLoading={isPreviewLoading}
                error={previewError}
                isConfirming={isConfirming}
            />
        </div>
    );
}

export default ManageExistingSportSubscription;

